import React, { useState, useCallback, useRef, useEffect, useLayoutEffect } from 'react';
import { View, TextInput, Pressable, StyleSheet, Platform, Keyboard } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { useChatInputStore, useAuthStore, useModalStore, useModelStore } from '@/store';
import { useTranslation } from '@/hooks/useTranslation';
import { trackEvent } from '@/constants/utils/analytics';
import { getShadowThemeStyles, isDarkTheme } from '@/constants/Colors';
import useResponsiveness from '@/hooks/useResponsiveness';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import ChatInputSendButton from '@/components/ChatInput/ChatInputSendButton';

const MEDIA_TYPES = [
  { id: 'image', icon: 'image', label: 'Image' },
  { id: 'video', icon: 'video', label: 'Video' },
  { id: 'music', icon: 'music', label: 'Music' },
  { id: '3d', icon: 'cube', label: '3D' },
];

const MIN_INPUT_H = 46;
const MAX_INPUT_H = 240;
const MIN_LYRICS_H = 64;
const MAX_LYRICS_H = 200;

function getWebTextAreaEl(ref) {
  const r = ref?.current;
  if (!r) return null;
  if (typeof window !== 'undefined' && r instanceof window.HTMLTextAreaElement) return r;
  if (r._node?.firstChild && r._node.firstChild.tagName === 'TEXTAREA') return r._node.firstChild;
  if (r._inputElement) return r._inputElement;
  const q = r.querySelector?.('textarea');
  return q || null;
}

const ExploreFloatingInput = React.memo(({ prefilledPrompt, prefilledMediaType, prefilledModelId, onClear }) => {
  const { colors, theme } = useTheme();
  const { t } = useTranslation();
  const { isXS } = useResponsiveness();
  const router = useRouter();
  const inputRef = useRef(null);
  const lyricsRef = useRef(null);
  const themeStyles = getShadowThemeStyles(theme);
  const placeholderColor = colors.color_input_placeholder
    || themeStyles.placeholderColor
    || colors.text_color_secondary;
  const mutedLabelColor = colors.text_color_secondary;

  const initialType = prefilledMediaType || 'image';
  const [selectedType, setSelectedType] = useState(initialType);
  const [text, setText] = useState(initialType === 'music' ? '' : (prefilledPrompt || ''));
  const [lyrics, setLyrics] = useState(initialType === 'music' ? (prefilledPrompt || '') : '');
  const [inputHeight, setInputHeight] = useState(MIN_INPUT_H);
  const [lyricsHeight, setLyricsHeight] = useState(MIN_LYRICS_H);

  const isMusic = selectedType === 'music';

  const token = useAuthStore(state => state.token);
  const isGhost = useAuthStore(state => state.user?.flags?.isGhost);
  const openModal = useModalStore(state => state.actions.openModal);

  const syncWebTextareaHeight = useCallback(() => {
    if (Platform.OS !== 'web') return;
    const el = getWebTextAreaEl(inputRef);
    if (!el || typeof el.scrollHeight !== 'number') return;
    el.style.height = `${MIN_INPUT_H}px`;
    const next = Math.min(Math.max(MIN_INPUT_H, el.scrollHeight), MAX_INPUT_H);
    el.style.height = `${next}px`;
    setInputHeight(next);
  }, []);

  const syncWebLyricsHeight = useCallback(() => {
    if (Platform.OS !== 'web') return;
    const el = getWebTextAreaEl(lyricsRef);
    if (!el || typeof el.scrollHeight !== 'number') return;
    el.style.height = `${MIN_LYRICS_H}px`;
    const next = Math.min(Math.max(MIN_LYRICS_H, el.scrollHeight), MAX_LYRICS_H);
    el.style.height = `${next}px`;
    setLyricsHeight(next);
  }, []);

  useLayoutEffect(() => {
    syncWebTextareaHeight();
  }, [text, syncWebTextareaHeight]);

  useLayoutEffect(() => {
    if (!isMusic) return;
    syncWebLyricsHeight();
  }, [lyrics, isMusic, syncWebLyricsHeight]);

  const handleTextChange = useCallback((val) => {
    setText(val);
  }, []);

  const handleLyricsChange = useCallback((val) => {
    setLyrics(val);
  }, []);

  const handleContentSizeChange = useCallback((e) => {
    if (Platform.OS === 'web') return;
    const h = e?.nativeEvent?.contentSize?.height;
    if (h) setInputHeight(Math.min(Math.max(MIN_INPUT_H, h), MAX_INPUT_H));
  }, []);

  const handleLyricsContentSizeChange = useCallback((e) => {
    if (Platform.OS === 'web') return;
    const h = e?.nativeEvent?.contentSize?.height;
    if (h) setLyricsHeight(Math.min(Math.max(MIN_LYRICS_H, h), MAX_LYRICS_H));
  }, []);

  const handleTypeToggle = useCallback((type) => setSelectedType(type), []);

  const handleSubmit = useCallback(async () => {
    const description = text.trim();
    const lyricsTrimmed = lyrics.trim();
    // For music, the top input (description) is the required style/mood and
    // lyrics are optional — leaving them blank kicks off an instrumental track.
    // For everything else, the top input IS the prompt and is required.
    if (isMusic) {
      if (!description) return;
    } else if (!description) {
      return;
    }
    const prompt = isMusic ? lyricsTrimmed : description;

    if (!token || isGhost) {
      trackEvent('explore', 'explore_auth_required', 'floating_input');
      if (isGhost) {
        openModal('GhostSignupModal');
      } else {
        openModal('SignupModal');
      }
      return;
    }

    if (Platform.OS !== 'web') Keyboard.dismiss();
    trackEvent('explore', 'explore_floating_input_submit', selectedType);

    // `prefilledModelId` actually holds an explore item's `modelSlug` (set by
    // handleTemplatePress in explore/index.jsx). Resolve it to a real model
    // _id before kicking off generation — otherwise createConversation fails
    // when the backend tries to ObjectId-cast a slug like "veo-3-1".
    let modelId = null;
    if (prefilledModelId) {
      try {
        const cached = useModelStore.getState().getModelBySlug?.(prefilledModelId);
        if (cached?.id) {
          modelId = cached.id;
        } else {
          const fetched = await useModelStore.getState().fetchModelBySlug?.(prefilledModelId);
          modelId = fetched?.id || null;
        }
      } catch {
        modelId = null;
      }
    }

    const store = useChatInputStore.getState();

    if (selectedType === 'image') {
      store.initializeImageGeneration(prompt, modelId, true);
    } else if (selectedType === 'video') {
      store.initializeVideoGeneration(prompt, modelId, true);
    } else if (selectedType === 'music') {
      store.initializeMusicGeneration(prompt, modelId, true, description || null);
    } else if (selectedType === '3d') {
      store.initialize3DGeneration(prompt, modelId, true);
    }

    router.push('/app');
    setText('');
    setLyrics('');
    setInputHeight(MIN_INPUT_H);
    setLyricsHeight(MIN_LYRICS_H);
    onClear?.();
  }, [text, lyrics, isMusic, selectedType, prefilledModelId, router, onClear, token, isGhost, openModal]);

  const handleWebKeyDown = useCallback((e) => {
    if (e.key !== 'Enter' || e.shiftKey) return;
    e.preventDefault();
    handleSubmit();
  }, [handleSubmit]);

  const handleLyricsWebKeyDown = useCallback((e) => {
    if (e.key !== 'Enter' || !(e.metaKey || e.ctrlKey)) return;
    e.preventDefault();
    handleSubmit();
  }, [handleSubmit]);

  useEffect(() => {
    if (!prefilledPrompt) return;
    if (prefilledMediaType === 'music') {
      if (prefilledPrompt !== lyrics) setLyrics(prefilledPrompt);
    } else if (prefilledPrompt !== text) {
      setText(prefilledPrompt);
    }
  }, [prefilledPrompt, prefilledMediaType]);

  useEffect(() => {
    if (prefilledMediaType) setSelectedType(prefilledMediaType);
  }, [prefilledMediaType]);

  // Music only requires a style/description; lyrics can be left blank for instrumentals.
  const hasText = !!text.trim();

  return (
    <View style={styles.outerContainer} pointerEvents="box-none">
      <View style={styles.innerWrapper} pointerEvents="box-none">
        <View
          pointerEvents="auto"
          style={[
            styles.inner,
        {
          borderColor: themeStyles.borderColor,
          backgroundColor: Platform.OS === 'web' ? themeStyles.backgroundColor : colors.solid_color_primary,
          borderRadius: isXS ? 24 : 24,
        },
        Platform.OS === 'web' && {
          backdropFilter: 'blur(18px) saturate(1.8)',
          WebkitBackdropFilter: 'blur(18px) saturate(1.8)',
          boxShadow: isDarkTheme(theme) ? themeStyles.boxShadow : 'none',
        },
      ]}>
        <View style={[styles.upperRow, Platform.OS === 'web' && { height: inputHeight }]}>
          <TextInput
            ref={inputRef}
            value={text}
            onChangeText={handleTextChange}
            placeholder={isMusic ? t('explore.musicDescriptionPlaceholder') : t('explore.typeToImagine')}
            placeholderTextColor={placeholderColor}
            style={[
              styles.textarea,
              {
                color: colors.text_color_primary,
                fontSize: isXS ? 15 : 14,
                ...(Platform.OS === 'web' ? { height: inputHeight } : { maxHeight: MAX_INPUT_H }),
              },
            ]}
            multiline
            scrollEnabled={Platform.OS !== 'web'}
            textAlignVertical="top"
            returnKeyType="default"
            blurOnSubmit={false}
            onContentSizeChange={handleContentSizeChange}
            {...(Platform.OS === 'web' ? { onKeyDown: handleWebKeyDown } : {})}
          />
        </View>
        {isMusic && (
          <View style={[styles.lyricsRow, { borderTopColor: colors.border_color_secondary }]}>
            <View style={styles.lyricsLabelRow}>
              <Icon name="music" size={10} color={mutedLabelColor} />
              <Text
                size="xxs"
                weight="semiBold"
                style={{ letterSpacing: 0.5, color: mutedLabelColor }}
              >
                {t('explore.lyricsLabel')}
              </Text>
            </View>
            <TextInput
              ref={lyricsRef}
              value={lyrics}
              onChangeText={handleLyricsChange}
              placeholder={t('explore.lyricsPlaceholder')}
              placeholderTextColor={placeholderColor}
              style={[
                styles.lyricsTextarea,
                {
                  color: colors.text_color_primary,
                  fontSize: isXS ? 14 : 13,
                  ...(Platform.OS === 'web' ? { height: lyricsHeight } : { maxHeight: MAX_LYRICS_H }),
                },
              ]}
              multiline
              scrollEnabled={Platform.OS !== 'web'}
              textAlignVertical="top"
              returnKeyType="default"
              blurOnSubmit={false}
              onContentSizeChange={handleLyricsContentSizeChange}
              {...(Platform.OS === 'web' ? { onKeyDown: handleLyricsWebKeyDown } : {})}
            />
          </View>
        )}
        <View style={[styles.lowerRow, { height: isXS ? 46 : 40 }]}>
          <View style={styles.mediaTypeRow}>
            {MEDIA_TYPES.map(mt => {
              const isActive = selectedType === mt.id;
              return (
                <Pressable
                  key={mt.id}
                  onPress={() => handleTypeToggle(mt.id)}
                  style={[
                    styles.mediaTypeButton,
                    {
                      backgroundColor: isActive ? colors.solid_color_secondary_dark : 'transparent',
                      borderColor: isActive ? colors.border_color_secondary : 'transparent',
                    },
                  ]}
                >
                  <Icon
                    name={mt.icon}
                    size={12}
                    color={isActive ? colors.text_color_primary : mutedLabelColor}
                  />
                  <Text
                    size="xxs"
                    weight={isActive ? 'medium' : 'regular'}
                    style={{ color: isActive ? colors.text_color_primary : mutedLabelColor }}
                  >
                    {mt.label}
                  </Text>
                </Pressable>
              );
            })}
          </View>
          <View style={styles.rightContainer}>
            <ChatInputSendButton
              onPress={handleSubmit}
              isDisabled={!hasText}
            />
          </View>
        </View>
        </View>
      </View>
    </View>
  );
});

const styles = StyleSheet.create({
  outerContainer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingBottom: Platform.OS === 'ios' ? 28 : 15,
  },
  innerWrapper: {
    width: '100%',
    maxWidth: 680,
    alignSelf: 'center',
  },
  inner: {
    flexDirection: 'column',
    overflow: 'hidden',
    borderWidth: 1,
    minHeight: 82,
  },
  upperRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    minHeight: MIN_INPUT_H,
    maxHeight: MAX_INPUT_H,
  },
  textarea: {
    flex: 1,
    width: '100%',
    backgroundColor: 'transparent',
    paddingVertical: 12,
    paddingRight: 12,
    paddingLeft: 16,
    lineHeight: 20,
    minHeight: MIN_INPUT_H,
    borderWidth: 0,
    ...(Platform.OS === 'web' && { outlineStyle: 'none', border: 'none', resize: 'none', WebkitAppearance: 'none' }),
  },
  lyricsRow: {
    paddingHorizontal: 16,
    paddingTop: 8,
    paddingBottom: 4,
    borderTopWidth: 1,
  },
  lyricsLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    marginBottom: 4,
  },
  lyricsTextarea: {
    width: '100%',
    backgroundColor: 'transparent',
    paddingVertical: 4,
    paddingHorizontal: 0,
    lineHeight: 18,
    minHeight: MIN_LYRICS_H,
    borderWidth: 0,
    ...(Platform.OS === 'web' && { outlineStyle: 'none', border: 'none', resize: 'none', WebkitAppearance: 'none' }),
  },
  lowerRow: {
    paddingHorizontal: 6,
    paddingBottom: 6,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  mediaTypeRow: {
    flexDirection: 'row',
    gap: 2,
  },
  mediaTypeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 5,
    borderRadius: 8,
    borderWidth: 1,
  },
  rightContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
});

export default ExploreFloatingInput;
